<script lang="ts">
	import GradientBorder from '$lib/util/GradientBorder.svelte';
	import {
		longOrShort,
		executingOrder,
		validInput,
		margin,
		accessibleMargin,
		newAccoutLeverage,
		executionType,
		limitOrderTriggerStatus,
		takeProfitActive,
		TakeProfitStatus,
		marginInputForTrade,
		userMarginBalanceForTrade,
		isTakeProfitInputValid,
		isStopLossInputValid,
		isAddMaxMargin
	} from './store';
	import {
		chain,
		smartWalletCreationPending,
		walletConnected,
		walletLoaded
	} from '$lib/wallet/store';
	import LongShort from './LongShort.svelte';
	import MoreInfoSection from './moreinfosection/MoreInfoSection.svelte';
	import MarginAndInput from './MarginAndInput.svelte';
	import ProcessingButton from '$lib/util/ProcessingButton.svelte';
	import { executeOrder, getTradeStatus } from './userInteract';
	import DisabledButton from '$lib/util/DisabledButton.svelte';
	import ConnectWalletButton from '$lib/util/ConnectWalletButton.svelte';
	import PriceInfoSection from './PriceInfoSection.svelte';
	import {
		currentMarketIdentifier,
		loadingPostTradeDetails,
		maxLeverage,
		ordersBeingTracked,
		postTradeDetails
	} from '../store';
	import { LimitStatus, TradeStatus } from '$lib/types/userInteract';
	import BigNumber from 'bignumber.js';
	import SwitchToOptimismButton from '$lib/util/SwitchToOptimismButton.svelte';
	import InsufficentScWalletBalanceButton from '$lib/util/InsufficentSCWalletBalanceButton.svelte';
	import OrderTypeSelector from './OrderTypeSelector.svelte';
	import { MIN_DEPOSIT_AMOUNT } from '$lib/settings';
	import CreateScWalletButton from '$lib/util/CreateSCWalletButton.svelte';
	import BedrockNote from './BedrockNote.svelte';
	import { isItBedrockTime } from '../util';
	import { loadingMatchaCallData } from '$lib/home/store';

	let isMarginInputAmountGTMax: boolean;

	$: tradeInvalid = $postTradeDetails?.status != TradeStatus.Ok;

	let pendingOrderInMarket: boolean = false;
	$: if ($ordersBeingTracked || $currentMarketIdentifier || $executionType) {
		if (Object.keys($ordersBeingTracked).length == 0 || $executionType == 'Limit') {
			pendingOrderInMarket = false;
		} else {
			let exists: boolean = false;
			Object.entries($ordersBeingTracked).forEach((e) => {
				if (
					['Pending', 'Expired'].includes(e[1].status) &&
					e[1].marketIdentifier == $currentMarketIdentifier
				) {
					exists = true;
				}
			});
			pendingOrderInMarket = exists;
		}
	}

	$: if ($executionType == 'Limit') {
		pendingOrderInMarket = false;
	}
	const disableOpeningTrade: boolean = false;

	$: walletNotConnected = $walletLoaded && !$walletConnected;
	$: notOptimismChain = $walletLoaded && $walletConnected && $chain?.id != 10;
	$: isInvalidTrade = !$loadingPostTradeDetails && $postTradeDetails && tradeInvalid;
	$: maxLeverageExceeded =
		!$loadingPostTradeDetails &&
		$postTradeDetails &&
		new BigNumber($newAccoutLeverage).gt($maxLeverage);
	$: insufficentScWalletBalance =
		!$loadingPostTradeDetails &&
		!!$postTradeDetails &&
		new BigNumber($postTradeDetails.fee)
			.plus(new BigNumber($postTradeDetails.keeperFee))
			.minus(new BigNumber($accessibleMargin))
			.gt(0) &&
		!$isAddMaxMargin;
	$: isInvalidLimitOrder = $executionType != 'Market' && $limitOrderTriggerStatus != LimitStatus.Ok;
</script>

<div class="sm:px-0 px-2 w-full">
	{#if isItBedrockTime()}
		<BedrockNote />
	{:else}
		<div
			class="w-full"
			data-title="Choose Long or Short"
			data-intro="Before we trade, you need to select whether you want to Long or Short the asset."
			data-step="3"
		>
			<LongShort />
		</div>
		<OrderTypeSelector />
		<MarginAndInput bind:isMarginInputAmountGTMax />
		<GradientBorder
			gradient="bg-fx-gradient-6"
			paddingClass="p-[0.5px]"
			borderRadius="rounded-xl squircle-xl"
			outerClassList="w-full mt-4"
			innerClassList="flex-col justify-center items-center bg-fx-grey-900 p-3 w-full"
		>
			<PriceInfoSection />

			{#if disableOpeningTrade}
				<DisabledButton title="Opening new trades disabled temporarily" />
			{:else if walletNotConnected}
				<ConnectWalletButton />
			{:else if notOptimismChain}
				<SwitchToOptimismButton />
			{:else if $smartWalletCreationPending}
				<CreateScWalletButton source="User Interact {$longOrShort}" />
			{:else if $margin && $margin == '0'}
				<DisabledButton title={'Enter Margin'} />
			{:else if isMarginInputAmountGTMax && !$isAddMaxMargin}
				<InsufficentScWalletBalanceButton />
			{:else if insufficentScWalletBalance}
				<InsufficentScWalletBalanceButton />
			{:else if !$validInput}
				<DisabledButton title={$longOrShort} />
			{:else if $executingOrder == true}
				<ProcessingButton title="Placing {$longOrShort} Order" />
			{:else if $loadingMatchaCallData}
				<ProcessingButton title="Fetching Swap details" />
			{:else if isInvalidTrade}
				<DisabledButton title={getTradeStatus(TradeStatus[$postTradeDetails.status])} />
			{:else if maxLeverageExceeded}
				<DisabledButton title="Max Leverage Exceeded" />
			{:else if pendingOrderInMarket}
				<DisabledButton title={'Order Pending'} />
			{:else if new BigNumber($margin).isLessThan(new BigNumber($MIN_DEPOSIT_AMOUNT).multipliedBy('1e18'))}
				<InsufficentScWalletBalanceButton title={'Min Margin is $50'} />
			{:else if new BigNumber($marginInputForTrade).isGreaterThan($userMarginBalanceForTrade) && !$isAddMaxMargin}
				<InsufficentScWalletBalanceButton />
			{:else if isInvalidLimitOrder}
				<DisabledButton title={getTradeStatus(LimitStatus[$limitOrderTriggerStatus])} />
			{:else if !$isTakeProfitInputValid}
				<DisabledButton title={'Invalid Take Profit'} />
			{:else if !$isStopLossInputValid}
				<DisabledButton title={'Invalid Stop Loss'} />
			{:else if $takeProfitActive && $TakeProfitStatus != LimitStatus.Ok}
				<DisabledButton
					title="Invalid Take Profit: {getTradeStatus(LimitStatus[$TakeProfitStatus])}"
				/>
			{:else}
				<button
					class={`w-full py-3 sm:py-4 text-fx-white ${
						$longOrShort == 'Long' ? 'bg-fx-gradient-23' : 'bg-fx-gradient-22'
					} rounded-xl squircle-xl font-semibold sm:text-sm text-xs`}
					on:click={executeOrder}
					data-testid="{$longOrShort.toLowerCase()}-btn"
				>
					{$longOrShort}
				</button>
			{/if}
			<MoreInfoSection />
		</GradientBorder>
	{/if}
</div>
